import * as React from "react";
import {
  Box,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  Popper,
  Paper,
  TextField,
  ClickAwayListener
} from "@mui/material";
import { useSelector } from "react-redux";
import StatefulButton from "../../../components/StatefulButton/StatefulButton";
import { tl } from "../../../components/translate";
import { RootState } from "../../../store";

// @ts-expect-error - css
import styles from "../Assessment.module.css";

interface Footer {
  sendToClient?: boolean;
  onSendToClientChange?: () => void;
  mode: string;
  onSave: () => void;
  navigateTo?: () => void;
  goBack?: () => void;
  printJSX: React.ReactNode;
  saveDisabled: boolean;
  goBackToClient?: () => void;
  onTemplateSave: (name: string) => void;
  onTemplateUpdate?: (name: string) => void;
  selectedTemplateId?: number;
  selectedTemplateName?: string;
}

const Footer = ({
  mode,
  onSave,
  saveDisabled,
  navigateTo,
  goBack,
  printJSX,
  goBackToClient,
  onTemplateSave,
  sendToClient = false,
  onSendToClientChange,
  selectedTemplateId,
  onTemplateUpdate,
  selectedTemplateName
}: Footer): React.JSX.Element => {
  const isSmsSubscribed = useSelector(
    (state: RootState) => state.userContext.resourceCentre.subscriptions.features.sms.subscribed
  );

  const [isLoading, setIsLoading] = React.useState(false);
  const [templateName, setTemplateName] = React.useState("");
  const [isTemplateLoading, setIsTemplateLoading] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [updateAnchorEl, setUpdateAnchorEl] = React.useState<null | HTMLElement>(null);

  const isNotViewMode = mode === "create" || mode === "edit" || mode === "copy";

  return (
    <Box className={styles.formFooter}>
      <Box>
        {mode === "view" && (
          <Box pr={2}>
            <Button
              data-testmation="backToClientButton"
              variant="text"
              color="primary"
              onClick={() => {
                if (goBackToClient) goBackToClient();
              }}
            >
              <Typography>{tl("assessment.backToClient")}</Typography>
            </Button>
          </Box>
        )}
      </Box>

      {isSmsSubscribed && isNotViewMode && (
        <Box mr="auto" ml={2}>
          <FormControlLabel
            control={
              <Checkbox defaultChecked checked={sendToClient} onChange={onSendToClientChange} />
            }
            label="Send message to client"
          />
        </Box>
      )}

      <Box display="flex">
        {isNotViewMode && (
          <Box
            sx={{
              display: "flex",
              gap: 2
            }}
          >
            <Button
              data-testmation="cancelButton"
              variant="text"
              color="primary"
              onClick={() => {
                if (goBack) goBack();
              }}
            >
              <Typography>{tl("cancel")}</Typography>
            </Button>

            {/* save as template button */}
            <Button
              variant="text"
              color="primary"
              disabled={isTemplateLoading}
              onClick={(e) => setAnchorEl(anchorEl ? null : e.currentTarget)}
              data-testmation="assessmentSaveAsTemplateButton"
            >
              <Typography>{tl("assessment.saveAsTemplate")}</Typography>
            </Button>
            {selectedTemplateId && (
              <Button
                variant="text"
                color="primary"
                disabled={isTemplateLoading}
                onClick={(e) => {
                  setTemplateName(selectedTemplateName || "");
                  setUpdateAnchorEl(updateAnchorEl ? null : e.currentTarget);
                }}
                data-testmation="assessmentUpdateTemplateButton"
              >
                <Typography>Update Selected Template</Typography>
              </Button>
            )}

            {/* same as template name popper */}
            <Popper
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              placement="top-end"
              sx={{ zIndex: 1 }}
            >
              <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                <Paper>
                  <Box
                    sx={{
                      width: 300,
                      padding: "15px",
                      border: "1px solid",
                      borderRadius: "10px",
                      borderColor: (theme) => theme.palette.divider,
                      display: "flex",
                      flexDirection: "column",
                      backgroundColor: "background.paper"
                    }}
                  >
                    <Typography variant="subtitle1">Template Name</Typography>
                    <TextField
                      autoFocus
                      fullWidth
                      placeholder="Provide the template name"
                      variant="outlined"
                      size="small"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      slotProps={{
                        inputLabel: { shrink: true }
                      }}
                      sx={{ margin: "5px 0 15px 0" }}
                    />

                    <Box display="flex" justifyContent="flex-end" gap={1}>
                      <Button onClick={() => setAnchorEl(null)} size="small">
                        Cancel
                      </Button>
                      <StatefulButton
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={!templateName.trim() || isTemplateLoading}
                        onClick={async () => {
                          setIsTemplateLoading(true);
                          await onTemplateSave(templateName);
                          setIsTemplateLoading(false);
                          setAnchorEl(null);
                          setTemplateName("");
                        }}
                        isLoading={isTemplateLoading}
                        circularProgressProps={{ size: 16 }}
                      >
                        Save
                      </StatefulButton>
                    </Box>
                  </Box>
                </Paper>
              </ClickAwayListener>
            </Popper>

            {/* update template popper */}
            <Popper
              open={Boolean(updateAnchorEl)}
              anchorEl={updateAnchorEl}
              placement="top-end"
              sx={{ zIndex: 1 }}
            >
              <ClickAwayListener onClickAway={() => setUpdateAnchorEl(null)}>
                <Paper>
                  <Box
                    sx={{
                      width: 300,
                      padding: "15px",
                      border: "1px solid",
                      borderRadius: "10px",
                      borderColor: (theme) => theme.palette.divider,
                      display: "flex",
                      flexDirection: "column",
                      backgroundColor: "background.paper"
                    }}
                  >
                    <Typography variant="subtitle1">Update Template Name</Typography>
                    <TextField
                      autoFocus
                      fullWidth
                      placeholder="Update template name"
                      variant="outlined"
                      size="small"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      slotProps={{
                        inputLabel: { shrink: true }
                      }}
                      sx={{ margin: "5px 0 15px 0" }}
                    />

                    <Box display="flex" justifyContent="flex-end" gap={1}>
                      <Button onClick={() => setUpdateAnchorEl(null)} size="small">
                        Cancel
                      </Button>
                      <StatefulButton
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={!templateName.trim() || isTemplateLoading}
                        onClick={async () => {
                          if (onTemplateUpdate) {
                            setIsTemplateLoading(true);
                            await onTemplateUpdate(templateName);
                            setIsTemplateLoading(false);
                            setUpdateAnchorEl(null);
                            setTemplateName("");
                          }
                        }}
                        isLoading={isTemplateLoading}
                        circularProgressProps={{ size: 16 }}
                      >
                        Update
                      </StatefulButton>
                    </Box>
                  </Box>
                </Paper>
              </ClickAwayListener>
            </Popper>

            {/* save button */}
            <StatefulButton
              variant="contained"
              color="primary"
              disabled={saveDisabled || isLoading}
              onClick={async () => {
                setIsLoading(true);
                await onSave();
                setIsLoading(false);
              }}
              isLoading={isLoading}
              circularProgressProps={{ size: 16 }}
              data-testmation="assessmentSaveButton"
            >
              <Typography>{tl("save")}</Typography>
            </StatefulButton>
          </Box>
        )}

        {mode === "view" && (
          <Box alignItems="center" display="flex">
            <Box pr={2}>
              <Button
                data-testmation="assessmentEditButton"
                variant="contained"
                color="primary"
                onClick={() => {
                  if (navigateTo) navigateTo();
                }}
              >
                <Typography>{tl("edit")}</Typography>
              </Button>
            </Box>
            <Box data-testmation="print">{printJSX}</Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Footer;
